import type { ApolloError } from '@apollo/client';
import { useDebounce } from 'ahooks';
import { useState } from 'react';
import type {
  StoredCostObject,
  StoredTeam,
  StoredTeamRole,
  StoredUser,
} from '../../../hooks/types';
import useQueryUser from '../../../hooks/useMatchedUsers';
import useQueryCostObjectIds from '../../../hooks/useQueryCostObjectIds';
import useQueryTeam from '../../../hooks/useQueryTeamForImtData';
import useQueryTeamRole from '../../../hooks/useQueryTeamRoleForImtData';
import useQueryUserTypes from '../../../hooks/useQueryUserTypes';
import { userFullName } from '../../../lib/utils';
import { roleGroupTypes, teamTypes } from '../../Teams/types';
import type { ExportDataSearchOption } from '../ExportDataSearch';

export const searchDebounceMillis = 300;

/**
 * Team search hooks for IMT data.
 */
export const useTeamSearchParams = (onError: (e: ApolloError) => void) => {
  const [teamSearchText, setTeamSearchText] = useState('');
  const [teamOptions, setTeamOptions] = useState<
    readonly ExportDataSearchOption[]
  >([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<readonly string[]>([]);

  const debouncedTeamSearchText = useDebounce(teamSearchText, {
    wait: searchDebounceMillis,
  });

  const { loading: loadingTeams } = useQueryTeam(
    debouncedTeamSearchText,
    (teams: readonly StoredTeam[]) => {
      const newTeamOptions: readonly ExportDataSearchOption[] =
        teamSearchText === ''
          ? []
          : teams.map((team) => ({
              label: `${team.name} ${team.internal_order_number ?? ''}`,
              value: team.id,
            }));

      setTeamOptions(newTeamOptions);
    },
    onError
  );

  return {
    loadingTeams,
    teamOptions,
    selectedTeamIds,
    setTeamSearchText,
    setSelectedTeamIds,
  };
};

/**
 * Team type search hooks for IMT data.
 */
export const useTeamTypeSearchParams = () => {
  const [selectedTeamTypes, setSelectedTeamTypes] = useState<readonly string[]>(
    []
  );
  const nonSocTeamType = teamTypes(true);
  const teamTypeOptions: readonly ExportDataSearchOption[] = [
    ...nonSocTeamType,
    'SOC',
  ].map((type) => ({ label: type, value: type }));

  return {
    teamTypeOptions,
    selectedTeamTypes,
    setSelectedTeamTypes,
  };
};

/**
 * Cost object search hooks for IMT data.
 */
export const useCostObjectSearchParams = (
  onError: (e: ApolloError) => void
) => {
  const [costObjectSearchText, setCostObjectSearchText] = useState('');
  const [costObjectOptions, setCostObjectOptions] = useState<
    readonly ExportDataSearchOption[]
  >([]);
  const [selectedCostObjectIds, setSelectedCostObjectIds] = useState<
    readonly string[]
  >([]);

  const debouncedCostObjectSearchText = useDebounce(costObjectSearchText, {
    wait: searchDebounceMillis,
  });

  const { loading: loadingCostObjects } = useQueryCostObjectIds(
    debouncedCostObjectSearchText,
    (costObjects: readonly StoredCostObject[]) => {
      const newCostObjectOptions: readonly ExportDataSearchOption[] =
        costObjectSearchText === ''
          ? []
          : costObjects.map((costObject) => ({
              label: `${costObject.internal_order_number} ${costObject.description}`,
              value: costObject.internal_order_number,
            }));

      setCostObjectOptions(newCostObjectOptions);
    },
    onError
  );

  return {
    loadingCostObjects,
    costObjectOptions,
    selectedCostObjectIds,
    setCostObjectSearchText,
    setSelectedCostObjectIds,
  };
};

/**
 * Team role search hooks for IMT data.
 */
export const useTeamRoleSearchParams = (onError: (e: ApolloError) => void) => {
  const [teamRoleSearchText, setTeamRoleSearchText] = useState('');
  const [teamRoleOptions, setTeamRoleOptions] = useState<
    readonly ExportDataSearchOption[]
  >([]);
  const [selectedTeamRoleIds, setSelectedTeamRoleIds] = useState<
    readonly string[]
  >([]);

  const debouncedTeamRoleSearchText = useDebounce(teamRoleSearchText, {
    wait: searchDebounceMillis,
  });

  const { loading: loadingTeamRoles } = useQueryTeamRole(
    debouncedTeamRoleSearchText,
    (teamRoles: readonly StoredTeamRole[]) => {
      const newTeamRoleOptions: readonly ExportDataSearchOption[] =
        teamRoleSearchText === ''
          ? []
          : teamRoles.map((teamRole) => ({
              label: `${teamRole.name} - ${teamRole.role_group ?? 'No group'}`,
              value: teamRole.id,
            }));

      setTeamRoleOptions(newTeamRoleOptions);
    },
    onError
  );

  return {
    loadingTeamRoles,
    teamRoleOptions,
    selectedTeamRoleIds,
    setTeamRoleSearchText,
    setSelectedTeamRoleIds,
  };
};

/**
 * Team role group search hooks for IMT data.
 */
export const useTeamRoleGroupSearchParams = () => {
  const [selectedTeamRoleGroups, setSelectedTeamRoleGroups] = useState<
    readonly string[]
  >([]);
  const roleGroupOptions: readonly ExportDataSearchOption[] =
    roleGroupTypes.map((type) => ({ label: type, value: type }));

  return {
    roleGroupOptions,
    selectedTeamRoleGroups,
    setSelectedTeamRoleGroups,
  };
};

/**
 * User search hooks for IMT data.
 */
export const useUserSearchParams = (onError: (e: ApolloError) => void) => {
  const [userSearchText, setUserSearchText] = useState('');
  const [userOptions, setUserOptions] = useState<
    readonly ExportDataSearchOption[]
  >([]);
  const [selectedUserIds, setSelectedUserIds] = useState<readonly string[]>([]);

  const debouncedUserSearchText = useDebounce(userSearchText, {
    wait: searchDebounceMillis,
  });

  const { loading: loadingUsers } = useQueryUser(
    debouncedUserSearchText,
    (users: readonly StoredUser[]) => {
      const newUserOptions: readonly ExportDataSearchOption[] =
        userSearchText === ''
          ? []
          : users.map((user) => ({
              label: `${userFullName(user)} (${user.user_name}) ${
                user.user_type ?? ''
              }`,
              value: user.id,
            }));

      setUserOptions(newUserOptions);
    },
    onError
  );

  return {
    loadingUsers,
    userOptions,
    selectedUserIds,
    setUserSearchText,
    setSelectedUserIds,
  };
};

/**
 * User type search hooks for IMT data.
 */
export const useUserTypeSearchParams = (onError: (e: ApolloError) => void) => {
  const [userTypes, setUserTypes] = useState<readonly string[]>([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState<readonly string[]>(
    []
  );
  const { loading: loadingUserTypes } = useQueryUserTypes(
    onError,
    setUserTypes
  );

  const userTypeOptions: readonly ExportDataSearchOption[] = userTypes.map(
    (type) => ({ label: type, value: type })
  );

  return {
    loadingUserTypes,
    userTypeOptions,
    selectedUserTypes,
    setSelectedUserTypes,
  };
};

/**
 * Shift search hooks for IMT data.
 */
export const useShiftSearchParams = () => {
  const todayDate = new Date();
  const [fromDate, setFromDate] = useState<Date>(todayDate);
  const [toDate, setToDate] = useState<Date>(todayDate);

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
  };
};
